import { Button, Tag, TaskCard } from "@wayflyer/flyui";
import { FundingApplicationSchemas } from "api-types";
import { useIntl, type IntlShape } from "react-intl";
import { useSpaNavigate } from "shared-ui";

import { getAirCta, getAirDescription, getAirTimeEstimate, getAirTitle } from "./card-content";
import { getCompletedAirResources } from "./completed-resources";
import { MESSAGES } from "./message-definitions";

export interface AIRListNextProps {
  airsData: FundingApplicationSchemas.AdditionalInformationRequestsData;
  airPath: string;
  // When the deal is owned by a Wayflyer rep we surface the rep-contact copy; otherwise we point the
  // customer at the email Wayflyer has sent. Mirrors the in-review screen's manual-AIR banner.
  isRepOwned: boolean;
}

// Todo: Breakout individual manual airs into their own cards with ONB-338
const ManualAirOnHoldCard = ({ intl, isRepOwned }: { intl: IntlShape; isRepOwned: boolean }) => (
  <TaskCard
    isCompleted={false}
    title={intl.formatMessage(MESSAGES.manualAirTitle)}
    subtitle={{
      description: intl.formatMessage(
        isRepOwned ? MESSAGES.manualAirDescriptionRepOwned : MESSAGES.manualAirDescriptionSelfServe
      ),
      // Empty subtext suppresses the time-estimate line; manual AIRs aren't actionable by the customer.
      subtext: "",
    }}
    slots={{
      end: <Tag color="info" content={intl.formatMessage(MESSAGES.manualAirTag)} />,
    }}
  />
);

export const AIRListNext = ({ airsData, airPath, isRepOwned }: AIRListNextProps) => {
  const intl = useIntl();
  const { navigate } = useSpaNavigate("FundingApp");
  const { airs, hasActiveManualAirs } = airsData;

  if (airs.length === 0 && !hasActiveManualAirs) {
    return null;
  }

  const sortedAirs = [...airs].sort((a, b) => {
    const aActive = a.status === "in_progress";
    const bActive = b.status === "in_progress";
    if (aActive && !bActive) {
      return -1;
    }
    if (!aActive && bActive) {
      return 1;
    }
    const titleCompare = getAirTitle(intl, a).localeCompare(getAirTitle(intl, b));
    if (titleCompare !== 0) {
      return titleCompare;
    }
    return a.public_identifier.localeCompare(b.public_identifier);
  });

  // Returns a fragment so callers can compose us inside their own Flex column alongside other task cards.
  return (
    <>
      {hasActiveManualAirs && <ManualAirOnHoldCard intl={intl} isRepOwned={isRepOwned} />}
      {sortedAirs.map((air) => {
        const title = getAirTitle(intl, air);
        if (air.status !== "in_progress") {
          const resources = getCompletedAirResources(intl, air);
          return (
            <TaskCard
              key={air.public_identifier}
              isCompleted={true}
              title={title}
              subtitle={resources.length > 0 ? { resources } : undefined}
              slots={{
                end: <Tag color="positive" content={intl.formatMessage(MESSAGES.completedTag)} />,
              }}
            />
          );
        }
        return (
          <TaskCard
            key={air.public_identifier}
            isCompleted={false}
            title={title}
            subtitle={{
              description: getAirDescription(intl, air),
              // defaultSubtext doubles as the fallback when an unknown AIR type has no time estimate.
              subtext: getAirTimeEstimate(intl, air) ?? intl.formatMessage(MESSAGES.defaultSubtext),
            }}
            slots={{
              end: (
                <Button variant="Tertiary" onClick={() => navigate(`${airPath}/${air.public_identifier}`)}>
                  {getAirCta(intl, air)}
                </Button>
              ),
            }}
          />
        );
      })}
    </>
  );
};
